import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { useIsMobile } from '../../../hooks';
import { MobileList } from '../../../components/common';
import api from '../../../services/api';
import { grey, primary, success, warning, error as errorColor, info } from '../../../styles/tokens';

/**
 * Waiting List (Admin)
 */
const WaitingList = () => {
  const { isMobile } = useIsMobile();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ total: 0, pending: 0, contacted: 0, converted: 0 });
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  // Filtres
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Menu actions
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);

  // Dialogs
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let url = `/admin/waiting-list?page=${pagination.page}&limit=20`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;

      const response = await api.get(url);

      setEntries(response.entries || []);
      setStats(response.stats || { total: 0, pending: 0, contacted: 0, converted: 0 });
      setPagination(prev => ({
        ...prev,
        pages: response.pagination?.pages || 1,
        total: response.pagination?.total || 0,
      }));
    } catch (err) {
      console.error('Erreur lors du chargement:', err);
      setError('Impossible de charger la liste d\'attente.');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, statusFilter, search]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  };

  const handleMenuOpen = (event, entry) => {
    setAnchorEl(event.currentTarget);
    setSelectedEntry(entry);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenEdit = () => {
    if (selectedEntry) {
      setEditStatus(selectedEntry.status);
      setEditNotes(selectedEntry.notes || '');
      setEditDialog(true);
      handleMenuClose();
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedEntry) return;

    try {
      setActionLoading(true);
      await api.put(`/admin/waiting-list/${selectedEntry._id}`, {
        status: editStatus,
        notes: editNotes,
      });
      setEditDialog(false);
      fetchEntries();
    } catch (err) {
      console.error('Erreur lors de la mise a jour:', err);
      setError('Impossible de mettre a jour l\'entree.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedEntry) return;

    try {
      setActionLoading(true);
      await api.delete(`/admin/waiting-list/${selectedEntry._id}`);
      setDeleteDialog(false);
      handleMenuClose();
      fetchEntries();
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      setError('Impossible de supprimer l\'entree.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      let url = `/admin/waiting-list/export`;
      if (statusFilter) url += `?status=${statusFilter}`;

      const response = await fetch(`${process.env.REACT_APP_API_URL}${url}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `waiting-list-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('Erreur lors de l\'export:', err);
      setError('Impossible d\'exporter la liste.');
    }
  };

  const getStatusChipProps = (status) => {
    const configs = {
      pending: { label: 'En attente', sx: { bgcolor: warning[50], color: warning.dark } },
      contacted: { label: 'Contacte', sx: { bgcolor: info[50], color: info.dark } },
      converted: { label: 'Converti', sx: { bgcolor: success[50], color: success.dark } },
      unsubscribed: { label: 'Desinscrit', sx: { bgcolor: errorColor[50], color: errorColor.dark } },
    };
    return configs[status] || { label: status || 'N/A', sx: { bgcolor: grey[100], color: grey[600] } };
  };

  return (
    <>
      <Helmet>
        <title>Waiting List - Admin Yoco</title>
      </Helmet>

      <Box>
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 3 }}
        >
          <Typography variant="h1">Waiting List</Typography>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon sx={{ fontSize: 18 }} />}
            onClick={handleExport}
          >
            Export CSV
          </Button>
        </Stack>

        {/* Stats */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <Card sx={{ flex: 1 }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary">Total</Typography>
              <Typography variant="h2">{stats.total}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary">En attente</Typography>
              <Typography variant="h2" sx={{ color: warning.dark }}>{stats.pending}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary">Contactes</Typography>
              <Typography variant="h2" sx={{ color: info.dark }}>{stats.contacted}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary">Convertis</Typography>
              <Typography variant="h2" sx={{ color: success.dark }}>{stats.converted}</Typography>
            </CardContent>
          </Card>
        </Stack>

        {/* Filtres */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                placeholder="Rechercher par email..."
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={handleSearch}
                sx={{ flexGrow: 1, maxWidth: 400 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: grey[400] }} />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Statut</InputLabel>
                <Select
                  value={statusFilter}
                  label="Statut"
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                >
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="pending">En attente</MenuItem>
                  <MenuItem value="contacted">Contacte</MenuItem>
                  <MenuItem value="converted">Converti</MenuItem>
                  <MenuItem value="unsubscribed">Desinscrit</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </CardContent>
        </Card>

        {/* Erreur */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Liste */}
        <Card className={isMobile ? 'mobile-list-container' : ''}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : entries.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body2">
                Aucune inscription trouvee.
              </Typography>
            </Box>
          ) : isMobile ? (
            /* Mobile: Liste en cards */
            <Box sx={{ p: 2 }}>
              <MobileList
                items={entries}
                renderItem={(entry) => {
                  const statusProps = getStatusChipProps(entry.status);
                  return {
                    primary: entry.email,
                    secondary: entry.notes || 'Aucune note',
                    meta: new Date(entry.createdAt).toLocaleDateString('fr-FR'),
                    chips: [
                      { label: statusProps.label, sx: statusProps.sx },
                    ],
                    onClick: () => {
                      setSelectedEntry(entry);
                      setEditStatus(entry.status);
                      setEditNotes(entry.notes || '');
                      setEditDialog(true);
                    },
                  };
                }}
                emptyMessage="Aucune inscription trouvee"
              />
            </Box>
          ) : (
            /* Desktop: Table classique */
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell>Inscription</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((entry) => {
                    const statusProps = getStatusChipProps(entry.status);
                    return (
                      <TableRow key={entry._id}>
                        <TableCell>
                          <Typography variant="body1">
                            {entry.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1">
                            {entry.source || 'landing'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={statusProps.label}
                            size="small"
                            sx={statusProps.sx}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: entry.notes ? grey[700] : grey[400],
                            }}
                          >
                            {entry.notes || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1">
                            {new Date(entry.createdAt).toLocaleDateString('fr-FR')}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, entry)}
                          >
                            <MoreVertIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={pagination.pages}
              page={pagination.page}
              onChange={(e, page) => setPagination(prev => ({ ...prev, page }))}
              color="primary"
            />
          </Box>
        )}

        {/* Menu actions */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleOpenEdit}>
            <EditIcon sx={{ fontSize: 18, mr: 1.5 }} />
            Modifier
          </MenuItem>

          <MenuItem onClick={() => setDeleteDialog(true)} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ fontSize: 18, mr: 1.5 }} />
            Supprimer
          </MenuItem>
        </Menu>

        {/* Dialog Edition */}
        <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Modifier l'inscription</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Email: <strong>{selectedEntry?.email}</strong>
              </Typography>

              <FormControl fullWidth size="small">
                <InputLabel>Statut</InputLabel>
                <Select
                  value={editStatus}
                  label="Statut"
                  onChange={(e) => setEditStatus(e.target.value)}
                >
                  <MenuItem value="pending">En attente</MenuItem>
                  <MenuItem value="contacted">Contacte</MenuItem>
                  <MenuItem value="converted">Converti</MenuItem>
                  <MenuItem value="unsubscribed">Desinscrit</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Notes"
                multiline
                rows={3}
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Ajouter des notes..."
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialog(false)} variant="outlined">
              Annuler
            </Button>
            <Button onClick={handleSaveEdit} variant="contained" disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={20} /> : 'Enregistrer'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog Suppression */}
        <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
          <DialogTitle>Supprimer l'inscription ?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Etes-vous sur de vouloir supprimer <strong>{selectedEntry?.email}</strong> de la liste d'attente ?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog(false)} variant="outlined">
              Annuler
            </Button>
            <Button onClick={handleDelete} color="error" variant="contained" disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={20} /> : 'Supprimer'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default WaitingList;
